export type LabelAnchor = 'start' | 'middle' | 'end'

export interface RadarLabelAnchor {
  textAnchor: LabelAnchor
  verticalAnchor: LabelAnchor
}

/** How far off the vertical a spoke must lean before its label hangs sideways. */
const SIDEWAYS_THRESHOLD = 0.1

/**
 * Where a radar spoke's label hangs from its point outside the web, so the text
 * grows away from the chart: right-side labels start at the point, left-side
 * labels end at it, and the top / bottom labels centre over / under it. Spoke
 * `index` of `count`, the first at 12 o'clock and the rest clockwise.
 */
export const getRadarLabelAnchor = (
  index: number,
  count: number
): RadarLabelAnchor => {
  const angle = count > 0 ? (index / count) * 2 * Math.PI : 0
  const horizontal = Math.sin(angle)
  if (horizontal > SIDEWAYS_THRESHOLD) {
    return { textAnchor: 'start', verticalAnchor: 'middle' }
  }
  if (horizontal < -SIDEWAYS_THRESHOLD) {
    return { textAnchor: 'end', verticalAnchor: 'middle' }
  }
  // Straight up or down: centred, growing away from the web.
  const isTop = Math.cos(angle) > 0
  return { textAnchor: 'middle', verticalAnchor: isTop ? 'end' : 'start' }
}

/**
 * Where spoke `index` of `count` reaches at `ratio` (0 centre – 1 outer ring)
 * of `radius` around (`cx`, `cy`): first spoke at 12 o'clock, then clockwise.
 */
export const getRadarPoint = (
  index: number,
  count: number,
  ratio: number,
  radius: number,
  cx: number,
  cy: number
): { x: number; y: number } => {
  const angle = count > 0 ? (index / count) * 2 * Math.PI : 0
  return {
    x: cx + Math.sin(angle) * ratio * radius,
    y: cy - Math.cos(angle) * ratio * radius,
  }
}
